class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.setThumbs = null;
    this.setImages = null;
    this.activeGalleryIsCustom = false;
    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.product = this.getJsonFromScript("product");
    this.variantTitles = this.getJsonFromScript("variant-titles") || {};
    this.defaultGallery = this.getJsonFromScript("default-gallery") || [];
    this.variantGalleries = this.getJsonFromScript("variant-galleries") || {};

    this.productForm = this.querySelector(".isProduct");
    if (!this.productForm || !this.product) return;

    const addButton = this.productForm.querySelector("#getCart");
    if (addButton && !addButton.dataset.enabledText) {
      const initialText = (addButton.textContent || "").trim();
      const looksDisabled =
        addButton.disabled ||
        initialText.toLowerCase() === "agotado" ||
        initialText.toLowerCase() === "sold out";
      addButton.dataset.enabledText = looksDisabled
        ? "Agregar"
        : initialText || "Agregar";
    }

    this.updateVariantClasses(this.productForm);
    this.initSwipers();

    this.productForm.addEventListener("change", this.handleChange);
    this.productForm.addEventListener("click", this.handleClick);

    const initialVariant = this.findSelectedVariant(this.productForm);
    if (initialVariant) {
      this.setSelectedVariantAvailability(this.productForm, initialVariant.available);
      this.updateGallery(initialVariant);
      this.updateProductInfo(initialVariant);
      this.updateColorName();
      this.updateVariantClasses(this.productForm);
    }
  }

  disconnectedCallback() {
    if (this.productForm) {
      this.productForm.removeEventListener("change", this.handleChange);
      this.productForm.removeEventListener("click", this.handleClick);
    }
    if (this.setImages) this.setImages.destroy(true, true);
    if (this.setThumbs) this.setThumbs.destroy(true, true);
  }

  getJsonFromScript(name) {
    const script = this.querySelector(`script[data-${name}-json]`);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn(`MainProduct: JSON inválido (${name}).`, error);
      return null;
    }
  }

  initSwipers() {
    const thumbsEl = this.querySelector("#product-thumbnails");
    const imagesEl = this.querySelector("#product-images");
    if (!thumbsEl || !imagesEl || typeof Swiper === "undefined") return;

    this.setThumbs = new Swiper(thumbsEl, {
      watchSlidesVisibility: true,
      watchSlidesProgress: true,
      watchOverflow: true,
      spaceBetween: 16,
      slidesPerView: 4.01,
      wrapperClass: "setItems",
      slideClass: "setItem",
      speed: 350,
      breakpoints: {
        640: { slidesPerView: 4.01 },
        1024: { slidesPerView: 6.01 },
      },
    });

    this.setImages = new Swiper(imagesEl, {
      slidesPerView: 1,
      wrapperClass: "setItems",
      slideClass: "setItem",
      zoom: true,
      thumbs: { swiper: this.setThumbs },
      pagination: {
        bulletActiveClass: "isActive",
        el: ".setDots",
        bulletClass: "setDot",
        clickable: true,
      },
    });
  }

  handleChange() {
    const selectedVariant = this.findSelectedVariant(this.productForm);
    if (selectedVariant) {
      this.updateURL(selectedVariant);
      this.updateVariantClasses(this.productForm);
      this.updateGallery(selectedVariant);
      this.updateProductInfo(selectedVariant);
      this.updateColorName();
    } else {
      this.setSelectedVariantAvailability(this.productForm, false);
      this.disableAddToCartButton(this.productForm);
    }
  }

  handleClick(event) {
    const label = event.target.closest("label.variant__color, label.variant__size");
    if (!label || !this.productForm) return;

    const inputId = label.getAttribute("for");
    if (!inputId) return;

    const input = this.productForm.querySelector(`#${CSS.escape(inputId)}`);
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  findSelectedVariant(form) {
    const selectedOptions = {};

    form
      .querySelectorAll('input[type="radio"][name^="options["]')
      .forEach((input) => {
        if (input.checked) {
          const match = input.name.match(/\[(.*)\]/);
          if (match) selectedOptions[match[1]] = input.value;
        }
      });

    return this.product.variants.find((variant) => {
      return Object.keys(selectedOptions).every((optionName) => {
        const optionIndex = this.product.options.indexOf(optionName);
        return selectedOptions[optionName] === variant.options[optionIndex];
      });
    });
  }

  updateURL(variant) {
    if (!window.history.replaceState) return;
    const url = new URL(window.location);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({ path: url.href }, "", url.href);
  }

  updateProductInfo(variant) {
    const priceElement = this.querySelector(".isPRC");
    const cantidadCuotasElement = this.querySelector(".cantidad-cuotas");
    const precioCuotasElement = this.querySelector(".precio-cuotas");
    const cantidadCuotas = cantidadCuotasElement
      ? Number(cantidadCuotasElement.innerText)
      : null;
    const hiddenInput = this.querySelector('input[name="id"]');

    if (cantidadCuotasElement && precioCuotasElement && cantidadCuotas) {
      const newPrecioCuotas = variant.price / cantidadCuotas;
      precioCuotasElement.innerText = this.formatPrice(newPrecioCuotas);
    }

    if (priceElement) priceElement.innerText = this.formatPrice(variant.price);
    if (hiddenInput) hiddenInput.value = variant.id;

    // SKU: only render when non-empty (create/remove on variant changes)
    const skuValue = variant && variant.sku ? String(variant.sku) : "";
    const headerBlock = this.querySelector("form.isProduct .setInput");
    const existingSkuP = headerBlock
      ? headerBlock.querySelector("p.isSKU")
      : this.querySelector("p.isSKU");
    const existingSkuCode = existingSkuP
      ? existingSkuP.querySelector(".sku-code")
      : null;

    if (!skuValue) {
      if (existingSkuP) existingSkuP.remove();
    } else if (existingSkuCode) {
      existingSkuCode.textContent = skuValue;
    } else if (headerBlock) {
      const skuP = document.createElement("p");
      skuP.className = "isSKU";
      skuP.append("Cod: ");
      const skuSpan = document.createElement("span");
      skuSpan.className = "sku-code";
      skuSpan.textContent = skuValue;
      skuP.appendChild(skuSpan);

      const insertBeforeNode =
        headerBlock.querySelector(".setPRC") ||
        headerBlock.querySelector(".isPRC") ||
        null;
      headerBlock.insertBefore(skuP, insertBeforeNode);
    }

    const tituloH1 = this.querySelector("form.isProduct .setInput h1");
    const nuevoTitulo = this.variantTitles[String(variant.id)];
    if (tituloH1 && nuevoTitulo) tituloH1.textContent = nuevoTitulo;

    const inputTituloVariante = this.querySelector("#lineItemTituloVariante");
    if (inputTituloVariante && nuevoTitulo) inputTituloVariante.value = nuevoTitulo;

    if (this.activeGalleryIsCustom) {
      if (this.setImages && this.setThumbs) {
        this.setImages.slideTo(0);
        this.setThumbs.slideTo(0);
      }
    } else if (this.setImages && this.setThumbs) {
      const url = new URL(window.location.href);
      const hasVariantInURL = url.searchParams.has("variant");

      let featuredImageId;
      if (hasVariantInURL && variant.featured_image) {
        featuredImageId = variant.featured_image.id;
      } else if (this.product.images && this.product.images[0]) {
        featuredImageId = this.product.images[0].id;
      }

      if (featuredImageId) {
        const newThumbSlide = this.querySelector(
          `.setThumbs .setItem[data-id="${featuredImageId}"]`
        );
        if (newThumbSlide) {
          const imageIndex = parseInt(newThumbSlide.dataset.index, 10);
          if (!isNaN(imageIndex)) {
            this.setImages.slideTo(imageIndex);
            this.setThumbs.slideTo(imageIndex);
          }
        }
      }
    }

    this.setSelectedVariantAvailability(this.productForm, variant.available);

    if (variant.available) {
      this.enableAddToCartButton(this.productForm);
    } else {
      this.disableAddToCartButton(this.productForm);
    }
  }

  updateVariantClasses(form) {
    const allVariantLabels = form.querySelectorAll(
      "label.variant__color, label.variant__size"
    );

    allVariantLabels.forEach((label) => {
      const inputId = label.getAttribute("for");
      const input = inputId ? form.querySelector(`#${CSS.escape(inputId)}`) : null;
      if (input && input.checked) label.classList.add("active");
      else label.classList.remove("active");
    });
  }

  updateColorName() {
    const activeColor = this.querySelector("label.active.variant__color");
    const colorNameElement = this.querySelector(".nombre-color");
    if (!activeColor || !colorNameElement) return;
    const nombreColor = activeColor.getAttribute("data-option-value");
    if (nombreColor) colorNameElement.innerText = nombreColor;
  }

  disableAddToCartButton(form) {
    const button = form.querySelector("#getCart");
    if (!button) return;
    button.disabled = true;
    button.innerText = "Agotado";
  }

  enableAddToCartButton(form) {
    const button = form.querySelector("#getCart");
    if (!button) return;
    const variantAvailable =
      form.dataset.variantAvailable !== "false" &&
      button.dataset.variantAvailable !== "false";
    const quantityAllowed = button.dataset.quantityAllowed !== "false";

    if (!variantAvailable) {
      this.disableAddToCartButton(form);
      return;
    }

    if (!quantityAllowed) {
      button.disabled = true;
      button.innerText = button.dataset.enabledText || "Agregar";
      return;
    }

    button.disabled = false;
    button.innerText = button.dataset.enabledText || "Agregar";
  }

  setSelectedVariantAvailability(form, isAvailable) {
    const button = form.querySelector("#getCart");
    const value = isAvailable ? "true" : "false";
    form.dataset.variantAvailable = value;
    if (button) button.dataset.variantAvailable = value;
  }

  getGalleryForVariant(variantId) {
    const variantGallery = this.variantGalleries[String(variantId)];
    if (Array.isArray(variantGallery) && variantGallery.length) {
      return { gallery: variantGallery, isCustom: true };
    }
    return { gallery: this.defaultGallery, isCustom: false };
  }

  updateGallery(variant) {
    const { gallery, isCustom } = this.getGalleryForVariant(variant.id);
    this.activeGalleryIsCustom = isCustom;
    this.rebuildGallery(gallery);
  }

  rebuildGallery(gallery) {
    const imagesWrap = this.querySelector("#product-images .setItems");
    const thumbsWrap = this.querySelector("#product-thumbnails .setItems");
    if (!imagesWrap || !thumbsWrap) return;

    imagesWrap.innerHTML = gallery
      .map((img, idx) => {
        const dataId = img.id ? ` data-id="${img.id}"` : "";
        return `
          <div class="setItem swiper-zoom-container" data-index="${idx}"${dataId}>
            <div class="setIMGs swiper-zoom-target" style="background-image: url(${img.full});"></div>
          </div>
        `;
      })
      .join("");

    thumbsWrap.innerHTML = gallery
      .map((img, idx) => {
        const dataId = img.id ? ` data-id="${img.id}"` : "";
        return `
          <div class="setItem" data-index="${idx}"${dataId}>
            <div class="setIMGs" style="background-image: url(${img.thumb});"></div>
          </div>
        `;
      })
      .join("");

    if (this.setImages) this.setImages.destroy(true, true);
    if (this.setThumbs) this.setThumbs.destroy(true, true);
    this.initSwipers();
  }

  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return `$${new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price)}`;
  }
}

if (!customElements.get("main-product")) {
  customElements.define("main-product", MainProduct);
}

